import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { API } from '../lib/api';
import { Star, Smartphone, KeyRound, CheckCircle2 } from 'lucide-react';

const Stars = ({ value, onChange, size = 'w-7 h-7' }) => (
  <div className="flex gap-1" data-testid="review-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="transition-transform hover:scale-110"
        data-testid={`review-star-${n}`}
        aria-label={`${n} star${n > 1 ? 's' : ''}`}
      >
        <Star className={`${size} ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
      </button>
    ))}
  </div>
);

const ReviewModal = ({ open, onOpenChange, targetType, targetId, targetName, onSubmitted }) => {
  const { toast } = useToast();
  const [stars, setStars] = useState(0);
  const [text, setText] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [otpSending, setOtpSending] = useState(false);

  // Detect logged-in customer (skips OTP)
  const customer = useMemo(() => {
    try {
      if (!localStorage.getItem('tgmart_token')) return null;
      const raw = localStorage.getItem('tgmart_user');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, [open]);
  const token = customer ? localStorage.getItem('tgmart_token') : null;

  useEffect(() => {
    if (!open) return;
    setStars(0); setText(''); setOtp(''); setDevOtp(null); setOtpStep(false);
    if (customer) {
      setReviewerName(customer.name || '');
      setMobile(customer.mobile || '');
    } else {
      setReviewerName(''); setMobile('');
    }
  }, [open, customer]);

  const sendOtp = async () => {
    if (!/^[0-9]{10}$/.test(mobile)) return toast({ title: 'Enter a 10-digit mobile', variant: 'destructive' });
    setOtpSending(true);
    try {
      const r = await axios.post(`${API}/reviews/send-otp`, { mobile });
      setOtpStep(true);
      if (r.data?.dev_otp) setDevOtp(r.data.dev_otp);
      toast({ title: 'OTP sent', description: r.data?.dev_otp ? `Dev OTP: ${r.data.dev_otp}` : `Sent to +91 ${mobile}` });
    } catch (err) {
      toast({ title: 'Failed to send OTP', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setOtpSending(false); }
  };

  const submit = async () => {
    if (stars < 1) return toast({ title: 'Please select a star rating', variant: 'destructive' });
    if (!reviewerName.trim()) return toast({ title: 'Your name is required', variant: 'destructive' });
    if (!customer && !otp) return toast({ title: 'Please verify your mobile first', variant: 'destructive' });
    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
      const body = {
        target_type: targetType, target_id: targetId,
        stars, text: text.trim(), reviewer_name: reviewerName.trim(),
        ...(customer ? {} : { mobile, otp }),
      };
      const r = await axios.post(`${API}/reviews/submit`, body, { headers });
      toast({
        title: r.data.replaced ? 'Review updated' : 'Review posted',
        description: 'Thanks for sharing your experience!',
      });
      onOpenChange(false);
      onSubmitted?.(r.data);
    } catch (err) {
      toast({ title: 'Could not post review', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#2c54b2]">Rate {targetType === 'vendor' ? 'this vendor' : 'this product'}</DialogTitle>
          <DialogDescription>
            {targetName && <span className="text-gray-700">{targetName}</span>}
            {customer && (
              <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5" /> Signed in — mobile verified
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-sm">Your Rating *</Label>
            <div className="mt-2">
              <Stars value={stars} onChange={setStars} />
            </div>
          </div>

          <div>
            <Label htmlFor="reviewer-name" className="text-xs">Your Name *</Label>
            <Input id="reviewer-name" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} maxLength={50} data-testid="reviewer-name-input" />
          </div>

          <div>
            <Label htmlFor="review-text" className="text-xs">Review</Label>
            <Textarea id="review-text" value={text} onChange={(e) => setText(e.target.value)} rows={3}
              placeholder="What did you like or dislike?" maxLength={500} data-testid="reviewer-text-input" />
            <div className="text-[10px] text-gray-400 text-right">{text.length}/500</div>
          </div>

          {!customer && (
            <div className="border-t pt-3 space-y-2">
              <Label className="text-sm">Mobile Verification *</Label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 border rounded-md bg-gray-50 text-sm">+91</div>
                <Input
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit mobile"
                  disabled={otpStep}
                  data-testid="review-mobile-input"
                />
                {!otpStep ? (
                  <Button type="button" onClick={sendOtp} disabled={otpSending || mobile.length !== 10} className="bg-[#2c54b2] hover:bg-[#1f3f87] text-white" data-testid="review-send-otp-btn">
                    <Smartphone className="w-4 h-4 mr-1" />
                    {otpSending ? 'Sending...' : 'Send OTP'}
                  </Button>
                ) : (
                  <Button type="button" variant="outline" onClick={() => { setOtpStep(false); setOtp(''); setDevOtp(null); }} data-testid="review-change-mobile">
                    Change
                  </Button>
                )}
              </div>
              {otpStep && (
                <div>
                  <Label htmlFor="review-otp" className="text-xs">Enter OTP</Label>
                  <Input id="review-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="4-digit OTP" className="text-center tracking-widest text-lg" data-testid="review-otp-input" />
                  {devOtp && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-1">Dev mode OTP: <b>{devOtp}</b></div>}
                </div>
              )}
            </div>
          )}

          <Button onClick={submit} disabled={submitting || stars < 1} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11" data-testid="review-submit-btn">
            <KeyRound className="w-4 h-4 mr-2" />{submitting ? 'Posting...' : 'Post review'}
          </Button>
          <p className="text-[11px] text-gray-500 text-center">
            Reviews are public. Your mobile number is verified but never displayed.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
